/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  getClientSummary,
  getClientTimeline,
} from "@services/requests/housien-services/ClientInsightsService";
import type {
  ClientSummary,
  ClientTimelineEvent,
} from "@services/requests/housien-services/ClientInsightsService";

interface UseClientOverviewParams {
  agencyId: string;
  clientId: string;
}

interface UseClientOverviewResult {
  summary: ClientSummary | null;
  timeline: ClientTimelineEvent[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

interface OverviewFetchArgs {
  agencyId: string;
  clientId: string;
  cancelRef: { current: { cancel: () => void }[] };
  cancelInFlight: () => void;
  setSummary: (v: ClientSummary | null) => void;
  setTimeline: (v: ClientTimelineEvent[]) => void;
  setIsLoading: (v: boolean) => void;
  setIsError: (v: boolean) => void;
}

/**
 * Loads client summary + timeline in parallel, cancelling stale requests.
 *
 * @param args - Identifiers, cancel handles, and state setters.
 * @returns A promise that resolves once the fetch settles.
 */
async function runOverviewFetch(args: OverviewFetchArgs): Promise<void> {
  if (!args.agencyId || !args.clientId) return;
  args.cancelInFlight();
  args.setIsLoading(true);
  args.setIsError(false);
  const summaryReq = getClientSummary(args.agencyId, args.clientId);
  const timelineReq = getClientTimeline(args.agencyId, args.clientId);
  args.cancelRef.current = [summaryReq, timelineReq];
  try {
    const [s, t] = await Promise.all([summaryReq, timelineReq]);
    args.setSummary(s);
    args.setTimeline(t);
  } catch {
    args.setIsError(true);
  } finally {
    args.setIsLoading(false);
  }
}

/**
 * Fetches and exposes a client's summary and activity timeline.
 *
 * @param params - The agency and client identifiers.
 * @returns The summary, timeline, load/error flags, and a refetch callback.
 */
export function useClientOverview({
  agencyId,
  clientId,
}: UseClientOverviewParams): UseClientOverviewResult {
  const shouldFetch = Boolean(agencyId && clientId);
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [timeline, setTimeline] = useState<ClientTimelineEvent[]>([]);
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const [isError, setIsError] = useState(false);
  const cancelRef = useRef<{ cancel: () => void }[]>([]);

  const cancelInFlight = useCallback(() => {
    cancelRef.current.forEach((p) => p.cancel());
    cancelRef.current = [];
  }, []);

  const fetchData = useCallback(
    () => runOverviewFetch({
      agencyId, clientId, cancelRef, cancelInFlight, setSummary, setTimeline, setIsLoading, setIsError,
    }),
    [agencyId, clientId, cancelInFlight],
  );

  useEffect(() => {
    if (!shouldFetch) {
      setSummary(null);
      setTimeline([]);
      setIsLoading(false);
      return () => {};
    }
    fetchData();
    return cancelInFlight;
  }, [shouldFetch, fetchData, cancelInFlight]);

  return { summary, timeline, isLoading, isError, refetch: fetchData };
}
